import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { FUEL_LOG_CREATE, FUEL_LOG_UPDATE, FUEL_LOG_GET, FUEL_LOG_GET_LIST_FILTER } from '../../graphql/fuelLogs';
import { VEHICLES_LIST } from '../../graphql/vehicles';
import { GUARDS_LIST } from '../../graphql/guards';
import { TextField, Button, MenuItem, Box, CircularProgress } from '@mui/material';
import EditableFormField from '../../components/editable-form-field';
import DatePickerAuto from '../../components/custom-datepicker/DatePickerAuto';

const FuelLogForm = ({ logId, onClose, isEditing: externalIsEditing, onToggleEdit }) => {
  // Get today's date in yyyy-mm-dd format
  const today = new Date().toISOString().slice(0, 10);
  const [internalIsEditing, setInternalIsEditing] = useState(!logId); // Start in edit mode for new logs, view mode for existing
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = onToggleEdit || setInternalIsEditing;
  
  const [form, setForm] = useState({
    vehicleId: '',
    date: logId ? '' : today, // Autofill with today if new log
    gallons: '',
    cost: '',
    odometer: '',
    fuelStation: '',
    notes: '',
  });

  const { data: vehiclesData, loading: vehiclesLoading } = useQuery(VEHICLES_LIST);
  const { data: guardsData, loading: guardsLoading } = useQuery(GUARDS_LIST);
  const { data: logData, loading: logLoading, refetch: refetchLogData } = useQuery(
    FUEL_LOG_GET,
    { 
      variables: { id: logId }, 
      skip: !logId,
      fetchPolicy: 'network-only'
    }
  );

  useEffect(() => {
    if (logId && logData?.fuelLog) {
      const log = logData.fuelLog;
      setForm({
        vehicleId: log.vehicle?.id || '',
        date: log.date ? log.date.split('T')[0] : '',
        gallons: log.gallons || '',
        cost: log.cost || '',
        odometer: log.odometer || '',
        fuelStation: log.fuelStation || '',
        notes: log.notes || '',
      });
    } else if (!logId) {
      setForm({
        vehicleId: '',
        date: today, // Autofill with today if new log
        gallons: '',
        cost: '',
        odometer: '',
        fuelStation: '',
        notes: '',
      });
    }
  }, [logId, logData, today]);


  const [createFuelLog, { loading: creating }] = useMutation(FUEL_LOG_CREATE, {
    refetchQueries: ['FuelLogsList'],
  });
  const [updateFuelLog, { loading: updating }] = useMutation(FUEL_LOG_UPDATE, {
    refetchQueries: ['FuelLogsList'],
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Only send the date part (YYYY-MM-DD) for 8base Date type
    let dateOnly = form.date ? form.date.split('T')[0] : form.date;
    const data = {
      date: dateOnly,
      gallons: parseFloat(form.gallons),
      cost: parseFloat(form.cost),
      odometer: parseFloat(form.odometer),
      fuelStation: form.fuelStation,
      notes: form.notes,
      ...(form.vehicleId && { vehicle: { connect: { id: form.vehicleId } } }),
    };
    if (logId) {
      await updateFuelLog({ variables: { filter: { id: logId }, data } });
    } else {
      await createFuelLog({ variables: { data } });
    }
    onClose(true);
  };

  const handleEdit = async () => {
    // Refetch the data to ensure we have the latest information
    if (logId && refetchLogData) {
      await refetchLogData();
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (logId) {
      // Reset form to original values
      const log = logData?.fuelLog;
      if (log) {
        setForm({
          vehicleId: log.vehicle?.id || '',
          date: log.date ? log.date.split('T')[0] : '',
          gallons: log.gallons || '',
          cost: log.cost || '',
          odometer: log.odometer || '',
          fuelStation: log.fuelStation || '',
          notes: log.notes || '',
        });
      }
      setIsEditing(false);
    } else {
      onClose();
    }
  };

  if (logId && logLoading) return <CircularProgress />;

  const vehicleOptions = vehiclesData?.vehiclesList?.items?.map(vehicle => ({
    value: vehicle.id,
    label: `${vehicle.make} ${vehicle.model} (${vehicle.licensePlate})`
  })) || [];

  // const driverOptions = guardsData?.guardsList?.items?.map(driver => ({
  //   value: driver.id,
  //   label: `${driver.firstName} ${driver.lastName}`
  // })) || [];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>

      {/* Edit Button for View Mode - Top */}
      {logId && !isEditing && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button onClick={handleEdit} variant="contained" color="primary">
            Edit
          </Button>
        </Box>
      )}

      <EditableFormField
        type="select"
        label="Vehicle"
        name="vehicleId"
        value={form.vehicleId}
        onChange={handleChange}
        isEditing={isEditing}
        options={vehicleOptions}
        disabled={vehiclesLoading}
      />

      {/* <EditableFormField
        type="select"
        label="Driver"
        name="driverId"
        value={form.driverId}
        onChange={handleChange}
        isEditing={isEditing}
        options={driverOptions}
        disabled={guardsLoading}
        required
      /> */}

      <DatePickerAuto
        editTab={isEditing}
        rest={{}}
        value={form.date}
        label="Date"
        sx={{width: '100%', mt: "16px", mb: "8px"}}
        onUpdate={(date) => {
          setForm(prev => ({ ...prev, date: date }));
        }}
        required
      />

      <EditableFormField
        type="number"
        label="Gallons"
        name="gallons"
        value={form.gallons}
        onChange={handleChange}
        isEditing={isEditing}
        required
      />

      <EditableFormField
        type="number"
        label="Cost"
        name="cost"
        value={form.cost}
        onChange={handleChange}
        isEditing={isEditing}
        required
      />

      <EditableFormField
        type="number"
        label="Odometer"
        name="odometer"
        value={form.odometer}
        onChange={handleChange}
        isEditing={isEditing}
        required
      />

      <EditableFormField
        type="text"
        label="Fuel Station"
        name="fuelStation"
        value={form.fuelStation}
        onChange={handleChange}
        isEditing={isEditing}
      />

      <EditableFormField
        type="textarea"
        label="Notes"
        name="notes"
        value={form.notes}
        onChange={handleChange}
        isEditing={isEditing}
        rows={3}
      />

      {/* Action Buttons - Bottom */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button onClick={handleCancel} sx={{ mr: 1 }} disabled={creating || updating}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={creating || updating}>
          {logId ? 'Update' : 'Create'}
        </Button>
      </Box>
    </Box>
  );
};

export default FuelLogForm; 